import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grow from "@mui/material/Grow";
import Link from "@mui/material/Link";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { nanoid } from "@reduxjs/toolkit";
import { Formik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Form from "react-bootstrap/Form";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import * as yup from "yup";

import ModalAllder from "components/layout/ModalAllder";
import {
  memberArInvoiceHandler,
  memberHandler,
  userBusinessGroupHandler,
} from "../../../redux/handlers";

import InfomationForm from "./components/InfomationForm";
import InfomationProduct from "./components/InfomationProduct";
import Payment from "./components/Payment";
import PrStep from "./components/Step";
import InformationSummary from "./section/InformationSummary";
import ModalPayment from "./modals/ModalPayment";
import ConfirmPaymentModal from "./modals/ConfirmPaymentModal";
import { PrintINV } from "./print/PrintINV";

const STATUS_STEP = {
  DRAFT: 0,
  PENDING_APPROVAL: 1,
  AWAITING_PAYMENT: 2,
  PAID: 3,
};

const HIDDEN_TAB_STATUSES = ["DRAFT", "PENDING_APPROVAL"];

const deepClone = (obj) => {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (Array.isArray(obj)) return obj.map(deepClone);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, deepClone(v)]),
  );
};

const openModal = (setter) => setter((p) => ({ ...p, open: true }));
const closeModal = (setter) => setter((p) => ({ ...p, open: false }));

const formatRejectDate = (raw) => {
  const d = new Date(raw);
  const thaiYear = d.getFullYear() + 543;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${thaiYear} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function mkModal(name, w = 360, h = 320) {
  return {
    open: false,
    x: 0,
    y: 0,
    width: w,
    height: h,
    pin: false,
    mode: "normal",
    modalname: name,
  };
}

const BLANK_ROW = () => ({
  rowID: nanoid(),
  documentNumber: "",
  documentDate: "",
  quantity: "",
  price_per_unit: "",
  discount_type: "amount",
  discount_rate: null,
  taxable_amount: "",
  vat: "",
  total_amount: "",
  remark: "",
  member_debt_transaction_id: null,
  is_checked: false,
  edit: false,
});

const INITIAL_FORM = {
  document_no: "",
  issue_date: null,
  due_date: null,
  reference: "",
  is_tax_invoice: true,
  discount_type: "amount",
  discount_rate: null,
  amount_before_discount: 0,
  discount_amount: 0,
  amount_after_discount: 0,
  taxable_amount_exempt: 0,
  taxable_amount_zero_vat: 0,
  taxable_amount_has_vat: 0,
  vat_amount: 0,
  grand_total: 0,
  remark: "",
  tag: [],
  status: "DRAFT",
};

function RejectBanner({ invoice }) {
  const isCanceled = invoice.status === "CANCELED";
  const bgColor = isCanceled
    ? "rgba(249, 80, 87, 0.2)"
    : "rgba(249, 193, 80, 0.2)";
  const textCls = isCanceled ? "text-red-600" : "text-orange-500";
  const iconColor = isCanceled ? "#F95057" : "#FF6B00";
  const dateStr = formatRejectDate(invoice.reject_at);
  const rejector = invoice.rejectedByUserBusinessGroup;
  const rejectorName = rejector
    ? `${rejector.first_name} ${rejector.last_name}`.trim()
    : "";

  return (
    <div
      className="mb-3 p-2 px-4 rounded-lg flex items-start gap-3 inline-flex"
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex-shrink-0 mt-0.5">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.2207 5.29036H11.054V7.1237H9.2207V5.29036ZM9.2207 8.95703H11.054V14.457H9.2207V8.95703ZM10.1374 0.707031C5.07737 0.707031 0.970703 4.8137 0.970703 9.8737C0.970703 14.9337 5.07737 19.0404 10.1374 19.0404C15.1974 19.0404 19.304 14.9337 19.304 9.8737C19.304 4.8137 15.1974 0.707031 10.1374 0.707031ZM10.1374 17.207C6.09487 17.207 2.80404 13.9162 2.80404 9.8737C2.80404 5.8312 6.09487 2.54036 10.1374 2.54036C14.1799 2.54036 17.4707 5.8312 17.4707 9.8737C17.4707 13.9162 14.1799 17.207 10.1374 17.207Z"
            fill={iconColor}
          />
        </svg>
      </div>
      <div className={`flex-1 text-sm ${textCls}`}>
        <span className="font-medium">
          {dateStr}
          {rejectorName && ` โดย ${rejectorName}`}
          {` "${invoice.remark}"`}
        </span>
      </div>
    </div>
  );
}

export default function DebtorManage() {
  const { businessSlug, invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNew = invoiceId === "new";

  const [invoice, setInvoice] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [ubgProfile, setUbgProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formdisabled, setFormdisabled] = useState(false);
  const [tab, setTab] = useState(1);
  const [open, setOpen] = useState(false);
  const [openApprove, setOpenApprove] = useState(false);
  const [productRow, setProductRow] = useState([]);
  const [initialValues, setInitialValues] = useState(INITIAL_FORM);

  const [backupData, setBackupData] = useState(null);
  const [backupProductRows, setBackupProductRows] = useState([]);

  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectDisposition, setRejectDisposition] = useState("REVERT_TO_DRAFT");

  const [modalSuccess, setModalSuccess] = useState(mkModal("success"));
  const [modalApproveRequestSuccess, setModalApproveRequestSuccess] = useState(
    mkModal("approveReq"),
  );
  const [modalApproveSuccess, setModalApproveSuccess] = useState(
    mkModal("approveOk", 460, 410),
  );
  const [modalNotApprove, setModalNotApprove] = useState(
    mkModal("notApprove", 460, 410),
  );
  const [modalValidation, setModalValidation] = useState(
    mkModal("validation", 460, 360),
  );
  const [validationErrors, setValidationErrors] = useState([]);
  const [modalPayment, setModalPayment] = useState(
    mkModal("modalPayment", 1000, 610),
  );
  const [modalCancelReceipt, setModalCancelReceipt] = useState(
    mkModal("cancelReceipt", 420, 280),
  );
  const [cancelReceiptTarget, setCancelReceiptTarget] = useState(null);
  const [modalDeletePayment, setModalDeletePayment] = useState(
    mkModal("deletePayment", 420, 260),
  );
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);
  const [modalPaymentMode, setModalPaymentMode] = useState("create");
  const [editPaymentData, setEditPaymentData] = useState(null);

  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingEditLocalId, setPendingEditLocalId] = useState(null);

  // ปิด modal โดยไม่บันทึก — ต้องเคลียร์ id รายการที่กำลังแก้ ไม่งั้นการชำระครั้งถัดไปจะไปทับรายการเดิม
  useEffect(() => {
    if (!modalPayment?.open) {
      setPendingEditLocalId(null);
    }
  }, [modalPayment?.open]);
  const [confirmPaymentModal, setConfirmPaymentModal] = useState(
    mkModal("confirmPayment", 520, 420),
  );
  const [confirmingSave, setConfirmingSave] = useState(false);

  const anchorRef = useRef(null);
  const handleCloseMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };
  const anchorRefApprove = useRef(null);
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "ใบแจ้งหนี้",
  });

  useEffect(() => {
    if (HIDDEN_TAB_STATUSES.includes(initialValues.status)) {
      setTab(1);
    }
  }, [initialValues.status]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const profile = await dispatch(
          userBusinessGroupHandler.getUserBusinessProfile(),
        );

        setUbgProfile(profile);

        if (isNew) {
          const docRes = await dispatch(
            memberArInvoiceHandler.getUniqueDocumentNo(
              businessSlug,
              INITIAL_FORM.is_tax_invoice,
            ),
          );
          if (docRes?.document_no) {
            setInitialValues((p) => ({
              ...p,
              document_no: docRes.document_no,
            }));
          }
          const memberId = searchParams.get("member_id");
          if (memberId) {
            const mem = await dispatch(memberHandler.getMemberById(memberId));
            setMemberData(mem);
          }
          setProductRow([BLANK_ROW(), BLANK_ROW(), BLANK_ROW()]);
        } else {
          const inv = await dispatch(
            memberArInvoiceHandler.getMemberArInvoiceById(invoiceId),
          );
          if (inv) {
            setInvoice(inv);
            setInitialValues({
              document_no: inv.document_no,
              issue_date: inv.issue_date ? new Date(inv.issue_date) : null,
              due_date: inv.due_date ? new Date(inv.due_date) : null,
              reference: inv.reference ?? "",
              is_tax_invoice: inv.is_tax_invoice,
              discount_type: inv.discount_type,
              discount_rate:
                inv.discount_rate != null ? Number(inv.discount_rate) : null,
              amount_before_discount: Number(inv.amount_before_discount),
              discount_amount: Number(inv.discount_amount),
              amount_after_discount: Number(inv.amount_after_discount),
              taxable_amount_exempt: Number(inv.taxable_amount_exempt),
              taxable_amount_zero_vat: Number(inv.taxable_amount_zero_vat),
              taxable_amount_has_vat: Number(inv.taxable_amount_has_vat),
              vat_amount: Number(inv.vat_amount),
              grand_total: Number(inv.grand_total),
              remark: inv.remark ?? "",
              tag: inv.tag ?? [],
              status: inv.status,
            });
            if (inv.member_id) {
              const mem = await dispatch(
                memberHandler.getMemberById(inv.member_id),
              );
              setMemberData(mem);
            }
            if (inv.items?.length) {
              setProductRow(
                inv.items.map((item) => ({
                  rowID: nanoid(),
                  id: item.id,
                  member_debt_transaction_id: item.member_debt_transaction_id,
                  quantity: item.quantity,
                  price_per_unit: Number(item.price_per_unit),
                  discount_type: item.discount_type,
                  discount_rate:
                    item.discount_rate != null
                      ? Number(item.discount_rate)
                      : null,
                  taxable_amount: Number(item.taxable_amount),
                  vat: Number(item.vat),
                  total_amount: Number(item.total_amount),
                  sort_order: item.sort_order,
                  remark: item.remark ?? "",
                  documentNumber:
                    item.memberDebtTransaction?.payment?.bill?.receipt_no ?? "",
                  documentDate:
                    item.memberDebtTransaction?.payment?.bill?.created_at ?? "",
                  is_checked: false,
                  edit: false,
                })),
              );
            } else {
              setProductRow([BLANK_ROW(), BLANK_ROW(), BLANK_ROW()]);
            }
            if (inv.status !== "DRAFT") setFormdisabled(true);
          }
        }
      } catch (err) {
        console.error("Error loading invoice:", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [dispatch, businessSlug, invoiceId, isNew, searchParams]);

  const addProductRow = useCallback((num) => {
    setProductRow((prev) => [
      ...prev,
      ...Array.from({ length: num }, BLANK_ROW),
    ]);
  }, []);

  // VAT inclusive: backed-out tax from gross price. Mirror of ModalLoadDocument.
  const calcVatRow = (price, isTaxInvoice) => {
    const p = Number(price) || 0;
    if (!isTaxInvoice || p === 0) {
      return { taxable_amount: p, vat: 0, total_amount: p };
    }
    const taxable = Math.round((p / 1.07) * 100) / 100;
    const vat = Math.round((p - taxable) * 100) / 100;
    return { taxable_amount: taxable, vat, total_amount: p };
  };

  const handleTaxInvoiceChange = useCallback(
    async (checked, setFieldValue) => {
      setFieldValue("is_tax_invoice", checked);
      setProductRow((prev) =>
        prev.map((row) => {
          const price = Number(row.price_per_unit);
          if (!price) return row;
          const { taxable_amount, vat, total_amount } = calcVatRow(
            price,
            checked,
          );
          return { ...row, taxable_amount, vat, total_amount };
        }),
      );
      if (isNew) {
        const docRes = await dispatch(
          memberArInvoiceHandler.getUniqueDocumentNo(businessSlug, checked),
        );
        if (docRes?.document_no) {
          setFieldValue("document_no", docRes.document_no);
        }
      }
    },
    [isNew, businessSlug, dispatch],
  );

  const buildItems = () =>
    productRow
      .filter((r) => r.price_per_unit !== "" && r.price_per_unit != null)
      .map((r, idx) => ({
        ...(r.id ? { id: r.id } : {}),
        member_debt_transaction_id: r.member_debt_transaction_id ?? null,
        quantity: Number(r.quantity) || 1,
        price_per_unit: Number(r.price_per_unit),
        discount_type: r.discount_type || "amount",
        discount_rate: r.discount_rate != null ? Number(r.discount_rate) : null,
        taxable_amount: Number(r.taxable_amount) || 0,
        vat: Number(r.vat) || 0,
        total_amount: Number(r.total_amount) || 0,
        sort_order: idx + 1,
        remark: r.remark || null,
      }));

  const memberId = memberData?.id ?? searchParams.get("member_id");
  const branchId =
    invoice?.branch_id ??
    ubgProfile?.userBusinessGroupBranch?.[0]?.branch_id ??
    null;

  const buildInvoicePayload = (values) => ({
    business_slug: businessSlug,
    branch_id: Number(branchId),
    member_id: Number(memberId),
    issue_date: values.issue_date,
    due_date: values.due_date,
    reference: values.reference || null,
    is_tax_invoice: values.is_tax_invoice,
    discount_type: values.discount_type,
    discount_rate: values.discount_rate,
    amount_before_discount: values.amount_before_discount,
    discount_amount: values.discount_amount,
    amount_after_discount: values.amount_after_discount,
    taxable_amount_exempt: values.taxable_amount_exempt,
    taxable_amount_zero_vat: values.taxable_amount_zero_vat,
    taxable_amount_has_vat: values.taxable_amount_has_vat,
    vat_amount: values.vat_amount,
    grand_total: values.grand_total,
    remark: values.remark || null,
    tag: values.tag ?? [],
    items: buildItems(),
  });

  const approvePayload = () => ({
    business_slug: businessSlug,
    approved_by_user_business_group_id: ubgProfile?.id,
  });

  const goBack = useCallback(() => {
    navigate(`/${businessSlug}/report/debtor/customer/${memberId}`);
  }, [navigate, businessSlug, memberId]);

  const createBackup = useCallback((currentFormData, currentProductRows) => {
    setBackupData(deepClone(currentFormData));
    setBackupProductRows(deepClone(currentProductRows));
  }, []);

  const restoreFromBackup = useCallback(
    (setFieldValue) => {
      if (!backupData) return;
      Object.keys(backupData).forEach((key) =>
        setFieldValue(key, backupData[key]),
      );
      setProductRow(backupProductRows);
    },
    [backupData, backupProductRows],
  );

  const clearBackup = useCallback(() => {
    setBackupData(null);
    setBackupProductRows([]);
  }, []);

  const validateBeforeAction = useCallback(
    async (values, validateForm, setTouched) => {
      const formErrors = await validateForm(values);
      const missing = [];
      if (formErrors.issue_date) missing.push("วันที่ออกเอกสาร");
      if (formErrors.due_date) missing.push("วันที่ครบกำหนด");
      if (!buildItems().length)
        missing.push("รายการเอกสาร (อย่างน้อย 1 รายการ)");

      if (missing.length > 0) {
        setTouched({ issue_date: true, due_date: true });
        setValidationErrors(missing);
        openModal(setModalValidation);
        return false;
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productRow],
  );

  const handleSaveDraft = async (values, { validateForm, setTouched } = {}) => {
    if (validateForm && setTouched) {
      if (!(await validateBeforeAction(values, validateForm, setTouched)))
        return;
    }
    try {
      setSaving(true);
      const sharedPayload = buildInvoicePayload(values);
      const result = await dispatch(
        isNew
          ? memberArInvoiceHandler.createMemberArInvoice({
              ...sharedPayload,
              document_no: values.document_no,
              created_by_user_business_group_id: ubgProfile?.id,
            })
          : memberArInvoiceHandler.updateMemberArInvoice(invoiceId, {
              ...sharedPayload,
              updated_by_user_business_group_id: ubgProfile?.id,
            }),
      );
      if (result) {
        setInvoice(result);
        setInitialValues((p) => ({ ...p, status: result.status }));
        if (!isNew) setFormdisabled(true);
        clearBackup();
        openModal(setModalSuccess);
        setTimeout(() => {
          closeModal(setModalSuccess);
          if (isNew && result.id)
            navigate(`/${businessSlug}/report/debtor/manage/${result.id}`);
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving invoice:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async (values, validateForm, setTouched) => {
    if (!(await validateBeforeAction(values, validateForm, setTouched))) return;
    try {
      setSaving(true);

      if (isNew) {
        const created = await dispatch(
          memberArInvoiceHandler.createMemberArInvoice({
            ...buildInvoicePayload(values),
            document_no: values.document_no,
            created_by_user_business_group_id: ubgProfile?.id,
            initial_status: "PENDING_APPROVAL",
          }),
        );
        if (!created?.id) return;
        setInvoice(created);
        setInitialValues((p) => ({ ...p, status: created.status }));
        setFormdisabled(true);
        openModal(setModalApproveRequestSuccess);
        setTimeout(() => closeModal(setModalApproveRequestSuccess), 1500);
        navigate(`/${businessSlug}/report/debtor/manage/${created.id}`);
        return;
      }

      const result = await dispatch(
        memberArInvoiceHandler.approveMemberArInvoice(
          invoice.id,
          approvePayload(),
        ),
      );
      if (result) {
        setInvoice(result);
        setInitialValues((p) => ({ ...p, status: result.status }));
        setFormdisabled(true);
        openModal(setModalApproveRequestSuccess);
        setTimeout(() => closeModal(setModalApproveRequestSuccess), 1500);
      }
    } catch (err) {
      console.error("Error submitting for approval:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!invoice?.id) return;
    try {
      setSaving(true);
      const result = await dispatch(
        memberArInvoiceHandler.approveMemberArInvoice(
          invoice.id,
          approvePayload(),
        ),
      );
      if (result) {
        setInvoice(result);
        setInitialValues((p) => ({ ...p, status: result.status }));
        openModal(setModalApproveSuccess);
      }
    } catch (err) {
      console.error("Error approving:", err);
    } finally {
      setSaving(false);
    }
  };

  // New invoice: single create with initial_status=AWAITING_PAYMENT.
  // Existing invoice: two approve calls (DRAFT→PENDING→AWAITING_PAYMENT).
  const handleDirectApprove = async (values, validateForm, setTouched) => {
    if (!(await validateBeforeAction(values, validateForm, setTouched))) return;
    try {
      setSaving(true);
      setOpen(false);

      if (isNew) {
        const created = await dispatch(
          memberArInvoiceHandler.createMemberArInvoice({
            ...buildInvoicePayload(values),
            document_no: values.document_no,
            created_by_user_business_group_id: ubgProfile?.id,
            initial_status: "AWAITING_PAYMENT",
          }),
        );
        if (!created?.id) return;
        setInvoice(created);
        setInitialValues((p) => ({ ...p, status: created.status }));
        setFormdisabled(true);
        openModal(setModalApproveSuccess);
        navigate(`/${businessSlug}/report/debtor/manage/${created.id}`);
        return;
      }

      const payload = approvePayload();
      const step1 = await dispatch(
        memberArInvoiceHandler.approveMemberArInvoice(invoice.id, payload),
      );
      if (!step1) return;
      const step2 = await dispatch(
        memberArInvoiceHandler.approveMemberArInvoice(invoice.id, payload),
      );
      if (step2) {
        setInvoice(step2);
        setInitialValues((p) => ({ ...p, status: step2.status }));
        setFormdisabled(true);
        openModal(setModalApproveSuccess);
      }
    } catch (err) {
      console.error("Error direct approving:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!invoice?.id) return;
    try {
      setSaving(true);
      const result = await dispatch(
        memberArInvoiceHandler.rejectMemberArInvoice(invoice.id, {
          business_slug: businessSlug,
          rejected_by_user_business_group_id: ubgProfile?.id,
          reject_disposition: rejectDisposition,
          remark: rejectRemark || null,
        }),
      );
      if (result) {
        setInvoice(result);
        setInitialValues((p) => ({ ...p, status: result.status }));
        if (result.status === "DRAFT") setFormdisabled(false);
        closeModal(setModalNotApprove);
      }
    } catch (err) {
      console.error("Error rejecting:", err);
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentSuccess = async (updatedInvoice) => {
    if (updatedInvoice) {
      const freshInvoice = await dispatch(
        memberArInvoiceHandler.getMemberArInvoiceById(updatedInvoice.id),
      );
      setInvoice(freshInvoice ?? updatedInvoice);
      setInitialValues((p) => ({ ...p, status: (freshInvoice ?? updatedInvoice).status }));
      setFormdisabled(true);
    }
    closeModal(setModalPayment);
    setEditPaymentData(null);
    setPendingEditLocalId(null);
    setModalPaymentMode("create");
  };

  const handleOpenCreatePayment = () => {
    setEditPaymentData(null);
    setPendingEditLocalId(null);
    setModalPaymentMode("create");
    openModal(setModalPayment);
  };

  const handleAddPayment = (body) => {
    if (pendingEditLocalId) {
      setPendingPayments((prev) =>
        prev.map((p) =>
          p._localId === pendingEditLocalId
            ? { ...body, _localId: pendingEditLocalId }
            : p,
        ),
      );
      setPendingEditLocalId(null);
    } else {
      setPendingPayments((prev) => [
        ...prev,
        { ...body, _localId: Date.now() + Math.random() },
      ]);
    }
    closeModal(setModalPayment);
    setEditPaymentData(null);
    setModalPaymentMode("create");
  };

  const handleEditPending = (pending) => {
    setEditPaymentData({
      completed_at: pending.payment_date,
      splits: pending.splits ?? [],
      feeAdjustments: pending.fee_adjustments ?? [],
      documentOffsets: pending.document_offsets ?? [],
      ar_withholding_tax_rate_id: pending.ar_withholding_tax_rate_id,
      withholding_tax_rate: pending.withholding_tax_rate,
    });
    setPendingEditLocalId(pending._localId);
    setModalPaymentMode("create");
    openModal(setModalPayment);
  };

  const handleRemovePending = (localId) => {
    setPendingPayments((prev) => prev.filter((p) => p._localId !== localId));
  };

  const handleConfirmPayments = async () => {
    if (!invoice?.id || pendingPayments.length === 0) return;
    setConfirmingSave(true);
    try {
      const payments = pendingPayments.map((payment) => {
        // eslint-disable-next-line no-unused-vars
        const { _localId, ...rawBody } = payment;
        return {
          ...rawBody,
          splits: (rawBody.splits ?? []).map(
            // eslint-disable-next-line no-unused-vars
            ({ paymentMethodName, _bankName, _bankBranchName, _bankAccountNumber, _bankAccountName, _bankImagePath, ...split }) => split,
          ),
        };
      });

      const result = await dispatch(
        memberArInvoiceHandler.manageMemberArPaymentBulk(invoice.id, payments),
      );

      if (result) {
        const freshInvoice = await dispatch(
          memberArInvoiceHandler.getMemberArInvoiceById(invoice.id),
        );
        setInvoice(freshInvoice ?? result);
        setInitialValues((p) => ({ ...p, status: (freshInvoice ?? result).status }));
        setFormdisabled(true);
      }
      setPendingPayments([]);
      closeModal(setConfirmPaymentModal);
    } catch (err) {
      console.error("Confirm payments error:", err);
    } finally {
      setConfirmingSave(false);
    }
  };

  const handleEditPayment = (receipt) => {
    setPendingEditLocalId(null);
    setEditPaymentData(receipt);
    setModalPaymentMode("edit");
    openModal(setModalPayment);
  };

  const handleDeletePayment = (receipt) => {
    setDeletePaymentTarget(receipt);
    openModal(setModalDeletePayment);
  };

  const handleConfirmDeletePayment = async () => {
    if (!deletePaymentTarget) return;
    try {
      const result = await dispatch(
        memberArInvoiceHandler.deleteMemberArPayment(
          invoice.id,
          deletePaymentTarget.id,
        ),
      );
      if (result) {
        const freshInvoice = await dispatch(
          memberArInvoiceHandler.getMemberArInvoiceById(invoice.id),
        );
        setInvoice(freshInvoice ?? result);
        setInitialValues((p) => ({ ...p, status: (freshInvoice ?? result).status }));
      }
    } catch (err) {
      console.error("Delete payment error:", err);
    } finally {
      closeModal(setModalDeletePayment);
      setDeletePaymentTarget(null);
    }
  };

  const handleIssueReceipt = async (receipt) => {
    try {
      const result = await dispatch(
        memberArInvoiceHandler.issueReceipt(invoice.id, receipt.id),
      );
      if (result) {
        const freshInvoice = await dispatch(
          memberArInvoiceHandler.getMemberArInvoiceById(invoice.id),
        );
        setInvoice(freshInvoice ?? result);
        setInitialValues((p) => ({ ...p, status: (freshInvoice ?? result).status }));
      }
    } catch (err) {
      console.error("Issue receipt error:", err);
    }
  };

  const handleCancelReceipt = (receipt) => {
    setCancelReceiptTarget(receipt);
    openModal(setModalCancelReceipt);
  };

  const handleConfirmCancelReceipt = async () => {
    if (!cancelReceiptTarget) return;
    try {
      const result = await dispatch(
        memberArInvoiceHandler.cancelReceipt(
          invoice.id,
          cancelReceiptTarget.id,
        ),
      );
      if (result) {
        const freshInvoice = await dispatch(
          memberArInvoiceHandler.getMemberArInvoiceById(invoice.id),
        );
        setInvoice(freshInvoice ?? result);
        setInitialValues((p) => ({ ...p, status: (freshInvoice ?? result).status }));
      }
    } catch (err) {
      console.error("Cancel receipt error:", err);
    } finally {
      closeModal(setModalCancelReceipt);
      setCancelReceiptTarget(null);
    }
  };

  if (loading)
    return <div className="text-center py-8">กำลังโหลดข้อมูล...</div>;

  const currentStatus = initialValues.status;

  // Approve/reject/cancel buttons: Super Admin (emp_business_role_id = null) or Business Admin (id = 1).
  const isAdmin =
    ubgProfile?.emp_business_role_id === 1 ||
    ubgProfile?.emp_business_role_id === null;

  return (
    <>
      <div className="mb-4">
        {/* Breadcrumb */}
        <Breadcrumbs separator="›" aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            href={`/${businessSlug}/report/debtor/list`}
            className="!text-sm"
          >
            รายงานลูกหนี้
          </Link>
          {memberData && (
            <Link
              underline="hover"
              color="inherit"
              href={`/${businessSlug}/report/debtor/customer/${memberData.id}`}
              className="!text-sm"
            >
              {`${memberData.first_name_th || ""} ${memberData.last_name_th || ""}`.trim() ||
                memberData.code}
            </Link>
          )}
          <Typography className="!text-sm pt-[2px]">
            {isNew ? "สร้างใบแจ้งหนี้" : initialValues.document_no}
          </Typography>
        </Breadcrumbs>

        <div className="flex items-center mt-2 mb-4">
          <Typography variant="h5">
            {isNew ? "สร้างใบแจ้งหนี้" : "ใบแจ้งหนี้"}
          </Typography>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={yup.object().shape({
            issue_date: yup.date().nullable().required(" "),
            due_date: yup.date().nullable().required(" "),
          })}
          enableReinitialize
          onSubmit={handleSaveDraft}
        >
          {({
            handleSubmit,
            handleChange,
            errors,
            values,
            setFieldValue,
            validateForm,
            setTouched,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <PrStep
                activeStep={STATUS_STEP[currentStatus] ?? 0}
                canceled={currentStatus === "CANCELED"}
              />

              {invoice?.reject_at && invoice?.remark && (
                <RejectBanner invoice={invoice} />
              )}

              <fieldset disabled={formdisabled}>
                <InfomationForm
                  values={values}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  errors={errors}
                  formdisabled={formdisabled}
                  memberData={memberData}
                  onTaxInvoiceChange={(checked) =>
                    handleTaxInvoiceChange(checked, setFieldValue)
                  }
                  status={currentStatus}
                  isNew={isNew}
                />
              </fieldset>

              {/* Tabs */}
              <div className="py-4">
                <Button
                  variant={tab === 1 ? "contained" : ""}
                  color="primary"
                  onClick={() => setTab(1)}
                >
                  ข้อมูลเอกสาร
                </Button>
                {currentStatus !== "DRAFT" &&
                  currentStatus !== "PENDING_APPROVAL" && (
                    <Button
                      variant={tab === 2 ? "contained" : ""}
                      color="primary"
                      onClick={() => {
                        // ถ้ากำลัง edit อยู่ → restore + lock ก่อน switch tab
                        if (!formdisabled) {
                          restoreFromBackup(setFieldValue);
                          setFormdisabled(true);
                          clearBackup();
                        }
                        setTab(2);
                      }}
                      className="ml-2"
                    >
                      การรับชำระ
                    </Button>
                  )}

                <fieldset disabled={tab === 2 ? false : formdisabled}>
                  <div className="py-4">
                    {tab === 1 && (
                      <>
                        <InfomationProduct
                          values={values}
                          setFieldValue={setFieldValue}
                          productRow={productRow}
                          setProductRow={setProductRow}
                          addProductRow={addProductRow}
                          formdisabled={formdisabled}
                          memberId={memberId}
                          businessSlug={businessSlug}
                        />
                        <InformationSummary
                          values={values}
                          setFieldValue={setFieldValue}
                          handleChange={handleChange}
                          errors={errors}
                          formdisabled={formdisabled}
                          productRow={productRow}
                        />
                      </>
                    )}
                    {tab === 2 && (
                      <Payment
                        modal={modalPayment}
                        setModal={setModalPayment}
                        invoice={invoice}
                        onEdit={handleEditPayment}
                        onDelete={handleDeletePayment}
                        onOpenCreate={handleOpenCreatePayment}
                        onIssueReceipt={handleIssueReceipt}
                        onCancelReceipt={handleCancelReceipt}
                        pendingPayments={pendingPayments}
                        onEditPending={handleEditPending}
                        onRemovePending={handleRemovePending}
                      />
                    )}
                  </div>
                </fieldset>
              </div>

              {/* Bottom bar */}
              <div className="py-10" />
              <div className="mb-3 shadow bg-white rounded-xl md:p-6 p-3 fixed bottom-[20px] left-3 md:left-[252px] right-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div style={{ display: "none" }}>
                      <PrintINV innerRef={componentRef} invoice={invoice} />
                    </div>
                    {!isNew &&
                      formdisabled &&
                      (currentStatus === "AWAITING_PAYMENT" ||
                        currentStatus === "PAID") && (
                        <Button
                          variant="contained"
                          color="blue2"
                          className="mx-3"
                          startIcon={<PrintIcon />}
                          onClick={handlePrint}
                        >
                          พิมพ์ใบแจ้งหนี้
                        </Button>
                      )}
                  </div>
                  <div className="flex items-center gap-2">
                    {formdisabled ? (
                      tab === 2 ? (
                        /* ── Tab การรับชำระ: ย้อนกลับ + ยืนยัน ── */
                        <>
                          <Button
                            variant="outlined"
                            color="gray"
                            onClick={goBack}
                          >
                            ย้อนกลับ
                          </Button>
                          <Button
                            variant="contained"
                            color="warning"
                            disabled={pendingPayments.length === 0}
                            onClick={() => openModal(setConfirmPaymentModal)}
                          >
                            ยืนยันการรับชำระเงิน
                          </Button>
                        </>
                      ) : (
                        /* ── Tab ข้อมูลเอกสาร: ปุ่มปกติ ── */
                        <>
                          <Button
                            variant="outlined"
                            color="gray"
                            onClick={goBack}
                          >
                            ย้อนกลับ
                          </Button>
                          {currentStatus === "DRAFT" && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                createBackup(values, productRow);
                                setFormdisabled(false);
                              }}
                            >
                              แก้ไข
                            </Button>
                          )}
                          {currentStatus === "PENDING_APPROVAL" && (
                            <>
                              {isAdmin && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => {
                                    createBackup(values, productRow);
                                    setFormdisabled(false);
                                  }}
                                >
                                  แก้ไข
                                </Button>
                              )}
                              {isAdmin && (
                                <>
                                  <ButtonGroup
                                    variant="contained"
                                    ref={anchorRefApprove}
                                    color="blue3"
                                    sx={{
                                      ".MuiButtonGroup-firstButton": {
                                        borderRight: 0,
                                      },
                                    }}
                                  >
                                    <Button
                                      disabled={saving}
                                      onClick={handleApprove}
                                    >
                                      อนุมัติ
                                    </Button>
                                    <Button
                                      className="!px-0"
                                      onClick={() => setOpenApprove((p) => !p)}
                                    >
                                      <ArrowDropDownIcon />
                                    </Button>
                                  </ButtonGroup>
                                  <Popper
                                    sx={{ zIndex: 1300, padding: "2px" }}
                                    open={openApprove}
                                    anchorEl={anchorRefApprove.current}
                                    role={undefined}
                                    transition
                                    placement="top-end"
                                  >
                                    {({ TransitionProps, placement }) => (
                                      <Grow
                                        {...TransitionProps}
                                        style={{
                                          transformOrigin:
                                            placement === "bottom"
                                              ? "center top"
                                              : "center bottom",
                                        }}
                                      >
                                        <Button
                                          variant="contained"
                                          color="error"
                                          disabled={saving}
                                          sx={{
                                            width:
                                              anchorRefApprove.current
                                                ?.offsetWidth ?? "auto",
                                            borderRadius: "8px 8px 8px 8px",
                                          }}
                                          onClick={() => {
                                            setOpenApprove(false);
                                            openModal(setModalNotApprove);
                                          }}
                                        >
                                          ไม่อนุมัติ
                                        </Button>
                                      </Grow>
                                    )}
                                  </Popper>
                                </>
                              )}
                            </>
                          )}
                          {currentStatus === "AWAITING_PAYMENT" && (
                            <>
                              {isAdmin && (
                                <Button
                                  variant="contained"
                                  color="error"
                                  onClick={() => openModal(setModalNotApprove)}
                                >
                                  ยกเลิกใบแจ้งหนี้
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  createBackup(values, productRow);
                                  setFormdisabled(false);
                                }}
                              >
                                แก้ไข
                              </Button>
                            </>
                          )}
                          {/* PAID / CANCELED — แสดงแค่ปุ่มย้อนกลับ (ไม่มีปุ่มแก้ไข) */}
                        </>
                      ) /* end tab === 1 branch */
                    ) : currentStatus === "PAID" ||
                      currentStatus === "CANCELED" ? (
                      // PAID / CANCELED ใน edit mode (edge case) → แสดงแค่ย้อนกลับ
                      <Button variant="outlined" color="gray" onClick={goBack}>
                        ย้อนกลับ
                      </Button>
                    ) : currentStatus === "AWAITING_PAYMENT" ||
                      currentStatus === "PENDING_APPROVAL" ? (
                      <>
                        <Button
                          variant="outlined"
                          color="gray"
                          onClick={
                            isNew
                              ? goBack
                              : () => {
                                  restoreFromBackup(setFieldValue);
                                  setFormdisabled(true);
                                  clearBackup();
                                }
                          }
                        >
                          ย้อนกลับ
                        </Button>
                        <Button
                          variant="contained"
                          color="blue3"
                          disabled={saving}
                          onClick={() => handleSubmit()}
                        >
                          บันทึก
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="gray"
                          onClick={
                            isNew
                              ? goBack
                              : () => {
                                  restoreFromBackup(setFieldValue);
                                  setFormdisabled(true);
                                  clearBackup();
                                }
                          }
                        >
                          ย้อนกลับ
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={saving}
                          onClick={() => handleSubmit()}
                        >
                          บันทึกร่าง
                        </Button>
                        {isAdmin ? (
                          <>
                            <ButtonGroup
                              variant="contained"
                              ref={anchorRef}
                              color="blue3"
                              sx={{
                                ".MuiButtonGroup-firstButton": {
                                  borderRight: 0,
                                },
                              }}
                            >
                              <Button
                                disabled={saving}
                                onClick={() =>
                                  handleSubmitForApproval(
                                    values,
                                    validateForm,
                                    setTouched,
                                  )
                                }
                              >
                                ส่งอนุมัติ
                              </Button>
                              <Button
                                size="small"
                                className="!px-0"
                                onClick={() => setOpen((p) => !p)}
                              >
                                <ArrowDropDownIcon />
                              </Button>
                            </ButtonGroup>
                            <Popper
                              sx={{ zIndex: 1300, padding: "2px" }}
                              open={open}
                              anchorEl={anchorRef.current}
                              role={undefined}
                              transition
                              placement="top-end"
                            >
                              {({ TransitionProps, placement }) => (
                                <Grow
                                  {...TransitionProps}
                                  style={{
                                    transformOrigin:
                                      placement === "bottom"
                                        ? "center top"
                                        : "center bottom",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    color="blue3"
                                    disabled={saving}
                                    sx={{
                                      width:
                                        anchorRef.current?.offsetWidth ??
                                        "auto",
                                      borderRadius: "8px 8px 8px 8px",
                                    }}
                                    onClick={() => {
                                      setOpen(false);
                                      handleDirectApprove(
                                        values,
                                        validateForm,
                                        setTouched,
                                      );
                                    }}
                                  >
                                    อนุมัติ
                                  </Button>
                                </Grow>
                              )}
                            </Popper>
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            color="blue3"
                            disabled={saving}
                            onClick={() =>
                              handleSubmitForApproval(
                                values,
                                validateForm,
                                setTouched,
                              )
                            }
                          >
                            ส่งอนุมัติ
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Modals */}
      <ModalAllder
        modal={modalSuccess}
        setModal={setModalSuccess}
        htitle="แจ้งเตือน"
        hbtn={false}
      >
        <div className="text-center py-6">
          <CheckCircleIcon className="!text-[100px] !text-[#2DC76D]" />
          <div
            className="fw-bold mt-2"
            style={{ fontSize: 22, color: "#2F4858" }}
          >
            บันทึกสำเร็จ
          </div>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalApproveRequestSuccess}
        setModal={setModalApproveRequestSuccess}
        htitle="แจ้งเตือน"
        hbtn={false}
      >
        <div className="text-center py-6">
          <CheckCircleIcon className="!text-[100px] !text-[#2DC76D]" />
          <div
            className="fw-bold mt-2"
            style={{ fontSize: 22, color: "#2F4858" }}
          >
            ส่งคำขออนุมัติสำเร็จ
          </div>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalApproveSuccess}
        setModal={setModalApproveSuccess}
        htitle="แจ้งเตือน"
        hbtn={false}
      >
        <div className="text-center py-6">
          <CheckCircleIcon className="!text-[100px] !text-[#2DC76D]" />
          <div
            className="mt-2"
            style={{ fontSize: 22, color: "rgba(76,78,100,0.87)" }}
          >
            คุณอนุมัติใบแจ้งหนี้
          </div>
          <div style={{ fontSize: 22, color: "#565DFF" }}>
            {initialValues.document_no}
          </div>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalNotApprove}
        setModal={setModalNotApprove}
        htitle="ยืนยันการไม่อนุมัติ"
        hbtn={false}
      >
        <div className="py-4">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">
              เหตุผลไม่อนุมัติ <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectRemark}
              onChange={(e) => setRejectRemark(e.target.value)}
            />
          </Form.Group>
          <Form.Label className="mb-2 tc-10">การดำเนินการ</Form.Label>
          <div className="mb-3">
            <Form.Check
              inline
              label="เปลี่ยนเป็นบันทึกร่าง"
              name="rd"
              type="radio"
              id="rd-draft"
              className="form-check-st-2 tc-7"
              value="REVERT_TO_DRAFT"
              checked={rejectDisposition === "REVERT_TO_DRAFT"}
              onChange={(e) => setRejectDisposition(e.target.value)}
            />
            <Form.Check
              inline
              label="ยกเลิกเอกสาร"
              name="rd"
              type="radio"
              id="rd-cancel"
              className="form-check-st-2 tc-7"
              value="CANCEL_DOCUMENT"
              checked={rejectDisposition === "CANCEL_DOCUMENT"}
              onChange={(e) => setRejectDisposition(e.target.value)}
            />
          </div>
          <div className="d-flex justify-center gap-3 pt-5 pb-4">
            <Button
              variant="outlined"
              color="gray"
              onClick={() => closeModal(setModalNotApprove)}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={handleReject}
            >
              บันทึก
            </Button>
          </div>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalValidation}
        setModal={setModalValidation}
        htitle="แจ้งเตือน"
        hbtn={false}
      >
        <div
          className="text-center fw-bold mt-10"
          style={{ fontSize: 22, color: "#2F4858" }}
        >
          กรุณากรอกข้อมูลให้ครบถ้วน
        </div>
        <div className="text-center mt-3 mb-2">
          {validationErrors.map((err) => (
            <div key={err} style={{ fontSize: 16, color: "#2F4858" }}>
              {err}
            </div>
          ))}
        </div>
        <div className="d-flex align-items-center justify-center pb-4 pt-5">
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={() => closeModal(setModalValidation)}
          >
            ตกลง
          </Button>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalCancelReceipt}
        setModal={setModalCancelReceipt}
        htitle="ยืนยันการยกเลิกใบเสร็จ"
        hbtn={false}
      >
        <div className="py-4">
          <div className="text-center mb-4" style={{ color: "#2F4858" }}>
            <div className="text-base">คุณต้องการยกเลิกใบเสร็จ</div>
            {cancelReceiptTarget?.memberArPaymentReceipt?.document_no && (
              <div
                className="font-semibold text-lg mt-1"
                style={{ color: "#F95057" }}
              >
                {cancelReceiptTarget.memberArPaymentReceipt.document_no}
              </div>
            )}
            <div
              className="text-sm mt-2"
              style={{ color: "rgba(76,78,100,0.68)" }}
            >
              การดำเนินการนี้จะยกเลิกเลขที่ใบเสร็จและสถานะการออกใบเสร็จ
            </div>
          </div>
          <div className="flex justify-center gap-3 pt-2 pb-2">
            <Button
              variant="outlined"
              color="gray"
              onClick={() => {
                closeModal(setModalCancelReceipt);
                setCancelReceiptTarget(null);
              }}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={saving}
              onClick={handleConfirmCancelReceipt}
            >
              ยืนยัน
            </Button>
          </div>
        </div>
      </ModalAllder>

      <ModalAllder
        modal={modalDeletePayment}
        setModal={setModalDeletePayment}
        htitle="ยืนยันการลบการชำระเงิน"
        hbtn={false}
      >
        <div className="py-4">
          <div className="text-center mb-4" style={{ color: "#2F4858" }}>
            <div className="text-base">คุณต้องการลบการชำระเงินครั้งนี้?</div>
            <div
              className="text-sm mt-2"
              style={{ color: "rgba(76,78,100,0.68)" }}
            >
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </div>
          </div>
          <div className="flex justify-center gap-3 pt-2 pb-2">
            <Button
              variant="outlined"
              color="gray"
              onClick={() => {
                closeModal(setModalDeletePayment);
                setDeletePaymentTarget(null);
              }}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={saving}
              onClick={handleConfirmDeletePayment}
            >
              ลบ
            </Button>
          </div>
        </div>
      </ModalAllder>

      <ConfirmPaymentModal
        modal={confirmPaymentModal}
        setModal={setConfirmPaymentModal}
        pendingCount={pendingPayments.length}
        onConfirm={handleConfirmPayments}
        saving={confirmingSave}
      />

      {createPortal(
        <ModalPayment
          modal={modalPayment}
          setModal={setModalPayment}
          invoice={invoice}
          businessSlug={businessSlug}
          ubgProfile={ubgProfile}
          memberData={memberData}
          onSuccess={handlePaymentSuccess}
          onAddPayment={handleAddPayment}
          mode={modalPaymentMode}
          editData={editPaymentData}
          pendingCount={pendingPayments.length}
          pendingAppliedAmount={pendingPayments
            .filter((p) => p._localId !== pendingEditLocalId)
            .reduce((sum, p) => sum + (Number(p.net_paid_amount) || 0), 0)}
          editingPendingReceiptNo={
            pendingEditLocalId
              ? (invoice?.paymentReceiptInvoices?.length ?? 0) +
                pendingPayments.findIndex(
                  (p) => p._localId === pendingEditLocalId,
                ) +
                1
              : null
          }
        />,
        document.body,
      )}
    </>
  );
}
